import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  last_login: string | null;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create user form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    is_staff: true,
    is_superuser: false,
  });
  const [isCreating, setIsCreating] = useState(false);

  // Edit user modal state
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    is_active: true,
    is_staff: true,
    is_superuser: false,
  });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminUsers();
      setUsers(data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to fetch admin users';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError(null);

    try {
      await apiService.createAdminUser(createForm);
      setSuccessMessage('User created successfully');
      setShowCreateForm(false);
      setCreateForm({
        username: '',
        password: '',
        email: '',
        first_name: '',
        last_name: '',
        is_staff: true,
        is_superuser: false,
      });
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create user';
      setError(errorMsg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditClick = (user: AdminUser) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      is_active: user.is_active,
      is_staff: user.is_staff,
      is_superuser: user.is_superuser,
    });
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setIsUpdating(true);
    setError(null);

    try {
      const updateData: any = {
        email: editForm.email,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        is_active: editForm.is_active,
        is_staff: editForm.is_staff,
        is_superuser: editForm.is_superuser,
      };

      // Only include password if it's provided
      if (editForm.password) {
        updateData.password = editForm.password;
      }

      await apiService.updateAdminUser(editingUser.id, updateData);
      setSuccessMessage('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update user';
      setError(errorMsg);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (user: AdminUser) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      return;
    }

    setError(null);
    try {
      await apiService.deleteAdminUser(user.id);
      setSuccessMessage('User deleted successfully');
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to delete user';
      setError(errorMsg);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-8 shadow-sm">
        <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
          <div>
            <h2 className="m-0 mb-1 text-slate-800 text-3xl font-semibold">Admin Users</h2>
            <p className="m-0 text-gray-500 text-base">Manage administrator accounts</p>
          </div>
        </div>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-8 shadow-sm">
      <div className="flex justify-between items-start mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="m-0 mb-1 text-slate-800 text-3xl font-semibold">Admin Users</h2>
          <p className="m-0 text-gray-500 text-base">Manage administrator accounts for the Update Manager portal</p>
        </div>
        <button
          className="bg-blue-600 text-white border-0 px-6 py-3 rounded cursor-pointer text-base font-medium transition-colors hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create User'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-800 px-5 py-4 rounded border border-red-200 mb-5 flex justify-between items-center">
          {error}
          <button onClick={() => setError(null)} className="bg-transparent border-0 text-red-800 cursor-pointer font-medium underline">Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 text-green-800 px-5 py-4 rounded border border-green-300 mb-5 flex justify-between items-center">
          {successMessage}
          <button onClick={() => setSuccessMessage(null)} className="bg-transparent border-0 text-green-800 cursor-pointer font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h4 className="m-0 mb-5 text-slate-800 text-lg">Create New Admin User</h4>
          <form onSubmit={handleCreateSubmit} className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="username" className="font-semibold text-slate-800 text-sm">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={createForm.username}
                  onChange={handleCreateInputChange}
                  required
                  disabled={isCreating}
                  className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="password" className="font-semibold text-slate-800 text-sm">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={createForm.password}
                  onChange={handleCreateInputChange}
                  required
                  disabled={isCreating}
                  minLength={6}
                  className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="first_name" className="font-semibold text-slate-800 text-sm">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={createForm.first_name}
                  onChange={handleCreateInputChange}
                  disabled={isCreating}
                  className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="last_name" className="font-semibold text-slate-800 text-sm">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={createForm.last_name}
                  onChange={handleCreateInputChange}
                  disabled={isCreating}
                  className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="font-semibold text-slate-800 text-sm">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={createForm.email}
                onChange={handleCreateInputChange}
                disabled={isCreating}
                className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
              />
            </div>
            <div className="flex gap-8 mt-2.5">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_staff"
                  name="is_staff"
                  checked={createForm.is_staff}
                  onChange={handleCreateInputChange}
                  disabled={isCreating}
                  className="w-auto m-0"
                />
                <label htmlFor="is_staff" className="m-0 font-normal">Staff Status</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_superuser"
                  name="is_superuser"
                  checked={createForm.is_superuser}
                  onChange={handleCreateInputChange}
                  disabled={isCreating}
                  className="w-auto m-0"
                />
                <label htmlFor="is_superuser" className="m-0 font-normal">Superuser</label>
              </div>
            </div>
            <div className="flex gap-2.5 justify-end mt-2.5 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                disabled={isCreating}
                className="px-5 py-2.5 border-0 rounded cursor-pointer text-sm font-medium transition-colors bg-gray-600 text-white hover:bg-gray-700"
              >
                Cancel
              </button>
              <button type="submit" disabled={isCreating} className="px-5 py-2.5 border-0 rounded cursor-pointer text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">
                {isCreating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="flex gap-5 mb-8 flex-wrap">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 min-w-[150px] flex-1">
          <h3 className="m-0 mb-2.5 text-sm text-gray-500 font-medium uppercase tracking-wider">Total Users</h3>
          <p className="m-0 text-3xl font-semibold text-slate-800">{users.length}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 min-w-[150px] flex-1">
          <h3 className="m-0 mb-2.5 text-sm text-gray-500 font-medium uppercase tracking-wider">Superusers</h3>
          <p className="m-0 text-3xl font-semibold text-slate-800">{users.filter(u => u.is_superuser).length}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 min-w-[150px] flex-1">
          <h3 className="m-0 mb-2.5 text-sm text-gray-500 font-medium uppercase tracking-wider">Active Users</h3>
          <p className="m-0 text-3xl font-semibold text-slate-800">{users.filter(u => u.is_active).length}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg shadow-sm">
        <table className="w-full border-collapse bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Username</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Name</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Email</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Status</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Role</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Last Login</th>
              <th className="px-3 py-4 text-left font-semibold text-slate-800 border-b-2 border-gray-200 text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={`border-b border-gray-200 transition-colors hover:bg-gray-50 ${!user.is_active ? 'opacity-60' : ''}`}>
                <td className="px-3 py-4 font-medium text-slate-800 max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap">{user.username}</td>
                <td className="px-3 py-4 text-gray-500 text-sm">
                  {user.first_name || user.last_name
                    ? `${user.first_name} ${user.last_name}`.trim()
                    : '-'}
                </td>
                <td className="px-3 py-4 text-gray-500 text-sm">{user.email || '-'}</td>
                <td className="px-3 py-4">
                  <span className={`inline-block px-2.5 py-1 rounded text-xs font-medium ${user.is_active ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-4">
                  {user.is_superuser ? (
                    <span className="bg-red-50 text-red-800 px-2 py-0.5 rounded text-xs">Superuser</span>
                  ) : user.is_staff ? (
                    <span className="bg-cyan-50 text-cyan-800 px-2 py-0.5 rounded text-xs">Staff</span>
                  ) : (
                    <span>User</span>
                  )}
                </td>
                <td className="px-3 py-4 text-gray-500 text-sm">{formatDate(user.last_login)}</td>
                <td className="px-3 py-4 text-center">
                  <button
                    className="bg-cyan-600 text-white border-0 px-3 py-1.5 rounded cursor-pointer text-xs mr-2 transition-colors hover:bg-cyan-700"
                    onClick={() => handleEditClick(user)}
                  >
                    Edit
                  </button>
                  <button
                    className="bg-red-600 text-white border-0 px-3 py-1.5 rounded cursor-pointer text-xs transition-colors hover:bg-red-700"
                    onClick={() => handleDeleteUser(user)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-10">
                  No admin users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex justify-center items-center z-[1000]" onClick={() => setEditingUser(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-[90%] max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h3 className="m-0 text-slate-800">Edit User: {editingUser.username}</h3>
              <button className="bg-transparent border-0 text-2xl cursor-pointer text-gray-500 p-0 w-8 h-8 flex items-center justify-center hover:text-slate-800" onClick={() => setEditingUser(null)}>
                &times;
              </button>
            </div>
            <div className="p-5">
              <form onSubmit={handleEditSubmit}>
                <div className="flex flex-col gap-1.5 mb-4">
                  <label htmlFor="edit_email" className="font-semibold text-slate-800 text-sm">Email</label>
                  <input
                    type="email"
                    id="edit_email"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditInputChange}
                    disabled={isUpdating}
                    className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="edit_first_name" className="font-semibold text-slate-800 text-sm">First Name</label>
                    <input
                      type="text"
                      id="edit_first_name"
                      name="first_name"
                      value={editForm.first_name}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="edit_last_name" className="font-semibold text-slate-800 text-sm">Last Name</label>
                    <input
                      type="text"
                      id="edit_last_name"
                      name="last_name"
                      value={editForm.last_name}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 mb-4">
                  <label htmlFor="edit_password" className="font-semibold text-slate-800 text-sm">New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    id="edit_password"
                    name="password"
                    value={editForm.password}
                    onChange={handleEditInputChange}
                    disabled={isUpdating}
                    minLength={6}
                    className="px-3 py-2.5 border border-gray-300 rounded text-sm transition-colors focus:outline-none focus:border-blue-600 focus:shadow-[0_0_0_2px_rgba(37,99,235,0.25)]"
                  />
                </div>
                <div className="flex gap-8 mt-2.5 mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit_is_active"
                      name="is_active"
                      checked={editForm.is_active}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="w-auto m-0"
                    />
                    <label htmlFor="edit_is_active" className="m-0 font-normal">Active</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit_is_staff"
                      name="is_staff"
                      checked={editForm.is_staff}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="w-auto m-0"
                    />
                    <label htmlFor="edit_is_staff" className="m-0 font-normal">Staff</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit_is_superuser"
                      name="is_superuser"
                      checked={editForm.is_superuser}
                      onChange={handleEditInputChange}
                      disabled={isUpdating}
                      className="w-auto m-0"
                    />
                    <label htmlFor="edit_is_superuser" className="m-0 font-normal">Superuser</label>
                  </div>
                </div>
                <div className="flex gap-2.5 justify-end mt-2.5 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    disabled={isUpdating}
                    className="px-5 py-2.5 border-0 rounded cursor-pointer text-sm font-medium transition-colors bg-gray-600 text-white hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={isUpdating} className="px-5 py-2.5 border-0 rounded cursor-pointer text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700">
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
